import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Check, ArrowRight, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import axios from 'axios';
import { useAuth, API } from '../App';
import { toast } from 'sonner';

const ChooseCoach = () => {
  const navigate = useNavigate();
  const { token, user, refreshUser } = useAuth();
  const [coaches, setCoaches] = useState([]);
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const fetchCoaches = async () => {
      try {
        const res = await axios.get(`${API}/coaches`);
        setCoaches(res.data.coaches);
      } catch (e) {
        console.error('Failed to load coaches:', e);
      }
      setLoading(false);
    };
    fetchCoaches();
  }, []);

  // If user already has a coach, redirect
  useEffect(() => {
    if (user?.coach) {
      navigate('/portal');
    }
  }, [user, navigate]);

  const selectCoach = async () => {
    if (!selected) return;
    setSaving(true);
    try {
      await axios.post(
        `${API}/auth/select-coach`,
        { coach_id: selected },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const coach = coaches.find(c => c.id === selected);
      toast.success(`${coach?.name} is now your coach!`);
      if (refreshUser) await refreshUser();
      navigate('/portal');
    } catch (error) {
      toast.error('Could not select coach');
    }
    setSaving(false);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-[#1E491A]" />
      </div>
    );
  }

  const females = coaches.filter(c => c.gender === 'female');
  const males = coaches.filter(c => c.gender === 'male');

  return (
    <div className="min-h-screen bg-white" data-testid="choose-coach-page">
      <div className="max-w-4xl mx-auto px-6 py-16">
        {/* Header */}
        <div className="text-center mb-12">
          <p className="text-sm font-bold uppercase tracking-widest text-[#1E491A]/60 mb-3" style={{ fontFamily: 'Arial, sans-serif' }}>
            Welcome to SPRING
          </p>
          <h1 className="text-4xl sm:text-5xl font-bold text-[#1E491A] mb-4" style={{ fontFamily: 'Arial, sans-serif' }}>
            Choose Your Coach
          </h1>
          <p className="text-lg text-[#1E491A]/80 max-w-xl mx-auto" style={{ fontFamily: 'Arial, sans-serif' }}>
            Your coach will guide you through the entire programme. Pick the one who feels right for you.
          </p>
        </div>

        {/* Coach Grid */}
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 sm:gap-6 mb-10">
          {[...females, ...males].map((coach) => {
            const isSelected = selected === coach.id;
            return (
              <button
                key={coach.id}
                onClick={() => setSelected(coach.id)}
                className={`
                  relative bg-white rounded-none p-5 text-center transition-all duration-200 border-3
                  ${isSelected 
                    ? 'border-[#1a1a1a]  scale-[1.02] ring-4 ring-[#1a1a1a]/10' 
                    : 'border-transparent shadow-md hover: hover:scale-[1.01]'
                  }
                `}
                data-testid={`coach-${coach.id}`}
              >
                {/* Selected check */}
                {isSelected && (
                  <div className="absolute top-3 right-3 w-7 h-7 bg-[#1E491A] rounded-none flex items-center justify-center">
                    <Check className="h-4 w-4 text-[#F6ADBC]" />
                  </div>
                )}

                {/* Avatar */}
                <div className="w-24 h-24 sm:w-28 sm:h-28 mx-auto mb-4 rounded-none overflow-hidden bg-[#F5F5F5] border-3 border-[#F0F0F0]">
                  <img
                    src={coach.avatar}
                    alt={coach.name}
                    className="w-full h-full object-cover"
                    loading="lazy"
                  />
                </div>

                {/* Name */}
                <h3 className="text-lg font-bold text-[#1E491A] mb-2" style={{ fontFamily: 'Arial, sans-serif' }}>
                  {coach.name}
                </h3>

                {/* Style description */}
                <p className="text-sm text-[#1E491A]/60 leading-relaxed" style={{ fontFamily: 'Arial, sans-serif' }}>
                  {coach.style}
                </p>
              </button>
            );
          })}
        </div>

        {/* CTA */}
        <div className="text-center">
          <Button
            onClick={selectCoach}
            disabled={!selected || saving}
            className={`
              h-14 px-10 rounded-none text-lg font-bold transition-all
              ${selected 
                ? 'bg-[#1E491A] text-[#F6ADBC] hover:bg-[#333] ' 
                : 'bg-[#1E491A]/20 text-[#1E491A]/40 cursor-not-allowed'
              }
            `}
            data-testid="confirm-coach-btn"
          >
            {saving ? (
              <Loader2 className="h-5 w-5 animate-spin mr-2" />
            ) : null}
            {selected 
              ? `Continue with ${coaches.find(c => c.id === selected)?.name}` 
              : 'Select a coach to continue'
            }
            {selected && <ArrowRight className="ml-2 h-5 w-5" />}
          </Button>
          <p className="text-sm text-[#1E491A]/50 mt-4" style={{ fontFamily: 'Arial, sans-serif' }}>
            You can change your coach anytime in Settings.
          </p>
        </div>
      </div>
    </div>
  );
};

export default ChooseCoach;
